import { X, Pencil, Link2, Pause, Play, Eye, MousePointer, Package } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCatalogDetail, useUpdateCatalogProduct } from '@/hooks/useCatalog';
import { Badge } from './ui/badge';
import { Card, CardContent, ErrorState } from '@mustauto/ui/web';
import { formatReais, formatNumber } from '@/lib/utils';

interface Props {
  productId: string;
  onClose: () => void;
  onEdit: () => void;
  onLinkToCampaign: () => void;
}

export function CatalogProductDrawer({ productId, onClose, onEdit, onLinkToCampaign }: Props) {
  const { user } = useAuth();
  const pid = user?.partnerId ?? '';
  const { data, isLoading, isError, error, refetch } = useCatalogDetail(pid, productId);
  const updateMutation = useUpdateCatalogProduct(pid);

  const product = data?.product;
  const linkedCampaigns = data?.campaigns ?? [];

  function toggleActive() {
    if (!product) return;
    updateMutation.mutate({ productId, active: !product.active });
  }

  // Erro antes do carregando: sem isto a gaveta fica em "Carregando..." para
  // sempre quando a busca falha, e nao ha nem botao de fechar visivel.
  if (isError && !product) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
        <div className="relative z-10 flex h-full w-full max-w-[480px] flex-col bg-background shadow-xl">
          <div className="flex justify-end p-4">
            <button
              type="button"
              aria-label="Fechar"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-muted"
            >
              <X size={18} />
            </button>
          </div>
          <ErrorState
            title="Não foi possível carregar o produto"
            error={error}
            onRetry={() => void refetch()}
          />
        </div>
      </div>
    );
  }

  if (isLoading || !product) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
        <div className="relative z-10 flex h-full w-[480px] items-center justify-center bg-background shadow-xl">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 flex h-full w-[480px] flex-col overflow-y-auto bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-bold">Detalhe do produto</h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted"><X size={18} /></button>
        </div>

        <div className="flex-1 space-y-4 p-4">
          {/* Photo + Name */}
          <div className="flex gap-4">
            {product.photoUrl ? (
              <img src={product.photoUrl} alt={product.name} className="h-24 w-24 rounded-lg object-cover" />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Package size={32} />
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-base font-bold">{product.name}</h3>
              {product.brand && <p className="text-sm text-muted-foreground">{product.brand}</p>}
              <div className="mt-1 flex gap-1.5">
                {product.outOfStock ? (
                  <Badge variant="warning">Sem estoque</Badge>
                ) : product.active ? (
                  <Badge variant="success">Ativo</Badge>
                ) : (
                  <Badge variant="secondary">Pausado</Badge>
                )}
                {product.activeCampaignsCount > 0 && (
                  <Badge variant="info">{product.activeCampaignsCount} campanhas</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted p-3">
            <div>
              <div className="text-xs text-muted-foreground">Preço</div>
              <div className="text-sm font-bold">
                {product.price ? formatReais(Number(product.price)) : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Prazo</div>
              <div className="text-sm font-medium">
                {product.deliveryDaysMin != null
                  ? `${product.deliveryDaysMin}${product.deliveryDaysMax ? `-${product.deliveryDaysMax}` : ''} dias`
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">SKU</div>
              <div className="text-sm">{product.sku ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Part Number</div>
              <div className="text-sm">{product.partNumber ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Categoria</div>
              <div className="text-sm">{product.category ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">URL</div>
              <div className="truncate text-sm">
                {product.buyUrl ? (
                  <a href={product.buyUrl} target="_blank" rel="noopener noreferrer"
                    className="text-primary hover:underline">{product.buyUrl}</a>
                ) : '—'}
              </div>
            </div>
          </div>

          {/* Compatibility */}
          {product.compatibleMakes && product.compatibleMakes.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Compatibilidade</div>
              <div className="flex flex-wrap gap-1">
                {product.compatibleMakes.map((m) => (
                  <Badge key={m} variant="outline">{m}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Tags</div>
              <div className="flex flex-wrap gap-1">
                {product.tags.map((t) => (
                  <Badge key={t} variant="secondary">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Campaigns using this product */}
          <div>
            <div className="text-sm font-bold mb-2">
              Campanhas ({linkedCampaigns.length})
            </div>
            {linkedCampaigns.length === 0 ? (
              /* Fica a mao: nota de uma linha sob um titulo que ja mostra a
                 contagem ("Campanhas (0)"), nao vazio de tela. */
              <p className="text-sm text-muted-foreground">Nenhuma campanha vinculada</p>
            ) : (
              <div className="space-y-2">
                {linkedCampaigns.map((c) => (
                  <div key={c.campaignProductId} className="flex items-center justify-between rounded-lg bg-muted p-2.5">
                    <div>
                      <div className="text-sm font-medium">{c.campaignName}</div>
                      {c.priceOverride && (
                        <div className="text-xs text-amber-600 dark:text-amber-400">
                          Preco override: {formatReais(Number(c.priceOverride))}
                        </div>
                      )}
                    </div>
                    <Badge variant={c.active ? 'success' : 'secondary'}>
                      {c.active ? 'Ativo' : 'Pausado'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Performance */}
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm font-bold mb-2">Desempenho acumulado</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Eye size={16} className="text-muted-foreground" />
                  <div>
                    <div className="text-lg font-bold">{formatNumber(product.totalImpressions)}</div>
                    <div className="text-xs text-muted-foreground">Impressões</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <MousePointer size={16} className="text-muted-foreground" />
                  <div>
                    <div className="text-lg font-bold">{formatNumber(product.totalClicks)}</div>
                    <div className="text-xs text-muted-foreground">Cliques</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="border-t p-4 flex gap-2">
          <button onClick={onEdit}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2.5 text-sm font-medium hover:bg-muted">
            <Pencil size={14} /> Editar
          </button>
          <button onClick={onLinkToCampaign}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2.5 text-sm font-medium hover:bg-muted">
            <Link2 size={14} /> Vincular
          </button>
          <button onClick={toggleActive} disabled={updateMutation.isPending}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2.5 text-sm font-medium hover:bg-muted disabled:opacity-50">
            {product.active ? <><Pause size={14} /> Pausar</> : <><Play size={14} /> Ativar</>}
          </button>
        </div>
      </div>
    </div>
  );
}
